import React, { useEffect, useState, useCallback } from 'react';
import api from '../../api';
import { toast } from 'react-toastify';

const MEDIA_BASE = 'https://api.alchemymix.com/channel_media';

function GetChannel({ onSelectChannel, onEditChannel, onDeleteChannel, refreshTrigger, setChannelData }) {
  const [channels, setChannels]           = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [deletingId, setDeletingId]       = useState(null); // which card is showing confirm
  const [loading, setLoading]             = useState(true);

  // ── Fetch channels ─────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    api.get('/get-channels', {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then((res) => {
        setChannels(res.data.channels || []);
        setLoading(false);
      })
      .catch((err) => {
        toast.error('Failed to load channels');
        setLoading(false);
        console.error(err);
      });
  }, [refreshTrigger]);

  // ── Sync selected channel to parent ───────────────────────
  useEffect(() => {
    if (selectedChannel && setChannelData) {
      const channel = channels.find(ch => ch._id === selectedChannel);
      if (channel) setChannelData(channel);
    }
  }, [selectedChannel, channels, setChannelData]);

  const handleSelect = useCallback((channel) => {
    setSelectedChannel(channel._id);
    if (onSelectChannel) onSelectChannel(channel);
  }, [onSelectChannel]);

  const handleEdit = useCallback((e, channel) => {
    e.stopPropagation(); // don't trigger card select
    if (onEditChannel) onEditChannel(channel);
  }, [onEditChannel]);

  const handleDeleteClick = useCallback((e, channelId) => {
    e.stopPropagation();
    setDeletingId(channelId); // show inline confirm
  }, []);

  const handleDeleteConfirm = useCallback(async (e, channel) => {
    e.stopPropagation();
    try {
      await api.delete(`/delete-channel/${channel._id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setChannels(prev => prev.filter(ch => ch._id !== channel._id));
      if (selectedChannel === channel._id) setSelectedChannel(null);
      toast.success(`"${channel.channelName}" deleted`);
      if (onDeleteChannel) onDeleteChannel(channel._id);
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Failed to delete channel');
    } finally {
      setDeletingId(null);
    }
  }, [selectedChannel, onDeleteChannel]);

  const handleDeleteCancel = useCallback((e) => {
    e.stopPropagation();
    setDeletingId(null);
  }, []);

  const handleImageError = useCallback((e) => {
    e.target.style.opacity = '0.3';
  }, []);

  if (loading) {
    return (
      <div className="get-channel">
        <h2>Select a Channel</h2>
        <div className="channel-list">
          {[1, 2, 3].map(i => <div key={i} className="channel-card channel-card--skeleton" />)}
        </div>
      </div>
    );
  }

  if (channels.length === 0) {
    return (
      <div className="get-channel">
        <h2>Select a Channel</h2>
        <div className="channel-empty">
          <span className="channel-empty-icon">📺</span>
          <p>No channels yet.</p>
          <span>Create your first channel to get started.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="get-channel">
      <h2>Select a Channel</h2>
      <div className="channel-list">
        {channels.map((channel) => {
          const isSelected  = selectedChannel === channel._id;
          const isDeleting  = deletingId === channel._id;

          return (
            <div
              key={channel._id}
              className={`channel-card ${isSelected ? 'selected' : ''} ${isDeleting ? 'channel-card--confirming' : ''}`}
              onClick={() => !isDeleting && handleSelect(channel)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !isDeleting) {
                  e.preventDefault();
                  handleSelect(channel);
                }
              }}
              aria-pressed={isSelected}
            >
              {/* ── Delete confirm overlay ──────────────── */}
              {isDeleting && (
                <div className="channel-card-confirm">
                  <p>Delete <strong>{channel.channelName}</strong>?</p>
                  <span>This cannot be undone.</span>
                  <div className="channel-card-confirm-btns">
                    <button className="ch-confirm-yes" onClick={(e) => handleDeleteConfirm(e, channel)}>
                      Delete
                    </button>
                    <button className="ch-confirm-no" onClick={handleDeleteCancel}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* ── Card content ────────────────────────── */}
              <img
                src={`${MEDIA_BASE}/${channel.logo}`}
                alt={`${channel.channelName} logo`}
                className="channel-logo"
                onError={handleImageError}
                loading="lazy"
              />
              <div className="channel-name">{channel.channelName}</div>
              <div className="channel-niche">{channel.niche}</div>

              {/* ── Action buttons ───────────────────────── */}
              <div className="channel-card-actions">
                <button
                  className="ch-action-btn ch-action-btn--edit"
                  onClick={(e) => handleEdit(e, channel)}
                  title="Edit channel"
                  aria-label={`Edit ${channel.channelName}`}
                >
                  ✏️
                </button>
                <button
                  className="ch-action-btn ch-action-btn--delete"
                  onClick={(e) => handleDeleteClick(e, channel._id)}
                  title="Delete channel"
                  aria-label={`Delete ${channel.channelName}`}
                >
                  🗑️
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default GetChannel;